import { useEffect, useState } from "react";
import { STYLES } from "./OnboardingGuidePage";

const FIRST_DAY_FEE = 1650;
const ADDITIONAL_DAY_FEE = 900;
const DAY_OPTIONS = [1, 2, 3, 4, 5];

const chapters = [
  ["remote-and-onsite", "Remote onboarding and on-site visits"],
  ["fees", "Trainer fees"],
  ["agenda", "What a visit covers"],
  ["travel", "Travel expenses"],
  ["request-quote", "Requesting a quote"],
];

const ashaFee = (days) => FIRST_DAY_FEE + ADDITIONAL_DAY_FEE * (days - 1);
const formatUsd = (amount) => `$${amount.toLocaleString("en-US")}`;

function Section({ index, children }) {
  const [id, title] = chapters[index];
  return (
    <section className="s" id={id} aria-labelledby={`${id}-heading`}>
      <div className="s-eyebrow">
        <span className="s-num">§ {String(index + 1).padStart(2, "0")}</span>
        <span className="s-rule" />
      </div>
      <h2 className="s-title" id={`${id}-heading`} tabIndex={-1}>{title}</h2>
      {children}
    </section>
  );
}

const scrollToQuoteSection = (event) => {
  event.preventDefault();
  const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  document.getElementById("request-quote")?.scrollIntoView({
    behavior: reduceMotion ? "auto" : "smooth",
    block: "start",
  });
  document.getElementById("request-quote-heading")?.focus({ preventScroll: true });
};

export default function OnsiteTrainingPricingPage() {
  const [days, setDays] = useState(2);

  useEffect(() => {
    const previousBg = document.body.style.background;
    const previousMargin = document.body.style.margin;
    const previousTitle = document.title;
    document.body.style.background = "#eef3fb";
    document.body.style.margin = "0";
    document.body.classList.add("guide-body");
    document.title = "Asha On-Site Training Pricing";
    return () => {
      document.body.style.background = previousBg;
      document.body.style.margin = previousMargin;
      document.body.classList.remove("guide-body");
      document.title = previousTitle;
    };
  }, []);

  const additionalDays = days - 1;

  return (
    <main className="guide-doc onsite-pricing">
      <style>{STYLES}{`
        .onsite-pricing .topbar { flex-wrap: wrap; gap: 14px; }
        .onsite-pricing h3.sub { display: block; margin-top: 30px; margin-bottom: 12px; line-height: 1.4; }
        .onsite-pricing h3.sub::before { content: none; display: none; }
        .onsite-pricing section[id] { scroll-margin-top: 24px; }
        .onsite-pricing .s-title:focus { outline: none; }
        .onsite-pricing .s-title:focus-visible { outline: 2px solid var(--accent-border); outline-offset: 4px; border-radius: 4px; }

        .onsite-pricing .hero-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 14px 20px; margin: 0 0 40px; }
        .onsite-pricing .cta {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 11px 20px; border-radius: 8px;
          background: var(--accent); color: #fff; border: 1px solid var(--accent-deep);
          font-size: 14.5px; font-weight: 600; letter-spacing: -0.005em;
          box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
          transition: background 120ms ease;
        }
        .onsite-pricing .cta:hover { background: var(--accent-deep); text-decoration: none; }
        .onsite-pricing .cta:focus-visible { outline: 2px solid var(--accent-border); outline-offset: 3px; }
        .onsite-pricing .cta-note { font-size: 13px; color: var(--ink-3); }

        .onsite-pricing .fee-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin: 0 0 28px; }
        .onsite-pricing .fee-card {
          padding: 26px 26px 24px; border: 1px solid var(--line); border-radius: 12px;
          background: var(--paper); display: flex; flex-direction: column;
        }
        .onsite-pricing .fee-card.primary {
          border-color: var(--accent-border);
          background: linear-gradient(180deg, var(--accent-soft), var(--paper) 62%);
        }
        .onsite-pricing .fee-label {
          font-size: 10.5px; letter-spacing: 0.12em; text-transform: uppercase;
          color: var(--accent-ink); font-weight: 600; margin-bottom: 14px;
        }
        .onsite-pricing .fee-name { font-size: 18px; font-weight: 600; color: var(--ink); letter-spacing: -0.01em; margin: 0 0 6px; }
        .onsite-pricing .fee-amount {
          font-size: 40px; font-weight: 700; letter-spacing: -0.03em; line-height: 1.1;
          color: var(--ink); margin: 14px 0 4px; font-variant-numeric: tabular-nums;
        }
        .onsite-pricing .fee-amount span { font-size: 15px; font-weight: 500; color: var(--ink-3); letter-spacing: 0; margin-left: 6px; }
        .onsite-pricing .fee-card p { font-size: 15px; line-height: 1.6; }
        .onsite-pricing .fee-card ul.simple { margin: 14px 0 0; padding-top: 14px; border-top: 1px solid var(--line-2); }
        .onsite-pricing .fee-card ul.simple li { font-size: 14.5px; line-height: 1.6; padding-top: 3px; padding-bottom: 3px; }
        .onsite-pricing .fee-card ul.simple li::before { top: 12px; }

        .onsite-pricing .estimator { border: 1px solid var(--line); border-radius: 12px; background: var(--paper-2); padding: 24px 26px; margin: 0 0 24px; }
        .onsite-pricing .estimator fieldset { border: 0; margin: 0; padding: 0; min-width: 0; }
        .onsite-pricing .estimator legend {
          padding: 0; margin-bottom: 12px; font-size: 10.5px; letter-spacing: 0.12em;
          text-transform: uppercase; color: var(--ink-3); font-weight: 600;
        }
        .onsite-pricing .segmented { display: inline-flex; flex-wrap: wrap; border: 1px solid var(--line); border-radius: 9px; background: var(--paper); padding: 3px; gap: 3px; }
        .onsite-pricing .segmented label { position: relative; }
        .onsite-pricing .segmented input { position: absolute; opacity: 0; width: 1px; height: 1px; pointer-events: none; }
        .onsite-pricing .segmented span {
          display: block; padding: 7px 14px; border-radius: 6px; cursor: pointer;
          font-size: 14px; font-weight: 500; color: var(--ink-2); white-space: nowrap;
        }
        .onsite-pricing .segmented span:hover { background: var(--line-3); }
        .onsite-pricing .segmented input:checked + span { background: var(--accent); color: #fff; }
        .onsite-pricing .segmented input:focus-visible + span { outline: 2px solid var(--accent-border); outline-offset: 2px; }
        .onsite-pricing .estimate-grid { display: grid; grid-template-columns: 1.3fr 1fr; gap: 24px; margin-top: 22px; padding-top: 20px; border-top: 1px solid var(--line); }
        .onsite-pricing .estimate-total { font-size: 32px; font-weight: 700; letter-spacing: -0.025em; color: var(--ink); line-height: 1.15; font-variant-numeric: tabular-nums; }
        .onsite-pricing .estimate-total small { display: block; font-size: 13px; font-weight: 400; color: var(--ink-3); letter-spacing: 0; margin-top: 6px; }
        .onsite-pricing .estimate-breakdown { margin: 0; font-size: 14px; color: var(--ink-2); font-variant-numeric: tabular-nums; }
        .onsite-pricing .estimate-breakdown div { display: flex; justify-content: space-between; gap: 16px; padding: 5px 0; border-bottom: 1px dashed var(--line); }
        .onsite-pricing .estimate-breakdown div:last-child { border-bottom: 0; }
        .onsite-pricing .estimate-breakdown dt { color: var(--ink-3); }
        .onsite-pricing .estimate-breakdown dd { margin: 0; font-weight: 500; color: var(--ink); }

        .onsite-pricing .agenda { list-style: none; margin: 0 0 22px; padding: 0; border-top: 1px solid var(--line-2); }
        .onsite-pricing .agenda li { display: grid; grid-template-columns: 200px 1fr; gap: 28px; padding: 14px 0; border-bottom: 1px solid var(--line-2); align-items: baseline; }
        .onsite-pricing .agenda b { font-size: 15px; font-weight: 600; color: var(--ink); }
        .onsite-pricing .agenda span { font-size: 15.5px; line-height: 1.65; color: var(--ink-2); }

        .onsite-pricing .quote-panel { margin: 4px 0 22px; padding: 24px 26px; border: 1px solid var(--accent-border); border-radius: 12px; background: var(--accent-soft); }
        .onsite-pricing .quote-panel ol { margin: 0; padding-left: 20px; }
        .onsite-pricing .quote-panel li { padding: 4px 0; font-size: 15.5px; line-height: 1.65; color: var(--ink-2); }
        .onsite-pricing .quote-panel li::marker { color: var(--accent-ink); font-weight: 600; }

        @media (max-width: 820px) {
          .onsite-pricing .fee-cards { grid-template-columns: 1fr; }
          .onsite-pricing .agenda li { grid-template-columns: 1fr; gap: 4px; }
          .onsite-pricing .estimate-grid { grid-template-columns: 1fr; gap: 16px; }
        }
        @media (max-width: 560px) {
          .onsite-pricing .fee-card, .onsite-pricing .estimator, .onsite-pricing .quote-panel { padding: 20px; }
          .onsite-pricing .fee-amount { font-size: 34px; }
          .onsite-pricing .estimate-total { font-size: 28px; }
          .onsite-pricing .segmented span { padding: 7px 11px; }
        }
        @media print {
          .onsite-pricing .hero-actions, .onsite-pricing .estimator { display: none; }
          .onsite-pricing .fee-card { break-inside: avoid; page-break-inside: avoid; }
        }
      `}</style>
      <div className="page">
        <div className="topbar">
          <div className="brand">
            <div className="brand-mark"><img src="/assets/alogo.png" alt="Asha" /></div>
            <span>Asha Health</span><span className="brand-meta">· asha.dental</span>
          </div>
          <span className="pill">On-site training</span>
        </div>

        <header className="hero">
          <div className="eyebrow">Asha AI Operating System · <b>On-site training</b></div>
          <h1 className="title">On-site training<br /><em>for your practice</em></h1>
          <p className="title-sub">An optional in-person visit from an Asha trainer, alongside your remote onboarding.</p>
          <p className="lede">Some offices want a trainer in the room with their staff for a day or more, working through real tasks side by side. This page sets out the fees for an on-site visit, what a visit covers, and how travel expenses are handled.</p>
          <div className="hero-actions">
            <a className="cta" href="#request-quote" onClick={scrollToQuoteSection}>Request a quote</a>
            <span className="cta-note">See how on-site visits are arranged.</span>
          </div>
          <div className="meta-grid">
            <div className="meta-cell"><div className="meta-label">First day</div><div className="meta-value">{formatUsd(FIRST_DAY_FEE)}<small>Seven hours, per trainer</small></div></div>
            <div className="meta-cell"><div className="meta-label">Each added day</div><div className="meta-value">{formatUsd(ADDITIONAL_DAY_FEE)}<small>Consecutive, seven hours</small></div></div>
            <div className="meta-cell"><div className="meta-label">Travel</div><div className="meta-value">Billed separately<small>Approved by your practice first</small></div></div>
            <div className="meta-cell"><div className="meta-label">Scheduling</div><div className="meta-value">Account Manager<small>Written quote before booking</small></div></div>
          </div>
        </header>

        <Section index={0}>
          <p className="s-sub">Standard onboarding is remote. An on-site visit is an optional addition for offices that want hands-on time in person.</p>
          <dl className="dl">
            <div><dt>Remote onboarding<small>Standard</small></dt><dd>Your Account Manager meets with your team twice a week for 30 minutes over twelve weeks, as described in the onboarding guide. This is how setup, training, and review normally happen.</dd></div>
            <div><dt>On-site visit<small>Optional</small></dt><dd>An Asha trainer spends one or more seven-hour days at your office, working with staff in their own workflows. It is priced separately and does not replace the remote meetings.</dd></div>
          </dl>
          <p>A visit can be useful when several staff members need to learn the same tools at once, or when a team prefers to practice with a trainer beside them. As with remote onboarding, the trainer teaches and guides; your team still enters, checks, and approves the office’s settings, rules, and instructions.</p>
        </Section>

        <Section index={1}>
          <p className="s-sub">Fees are per trainer and cover the trainer’s time on site. Travel is billed separately.</p>
          <div className="fee-cards">
            <article className="fee-card primary" aria-labelledby="fee-first-day">
              <div className="fee-label">Single-day visit</div>
              <h3 className="fee-name" id="fee-first-day">First on-site day</h3>
              <div className="fee-amount">{formatUsd(FIRST_DAY_FEE)}<span>per trainer</span></div>
              <p>One seven-hour training day at your office, planned around the tools and roles in your onboarding plan.</p>
              <ul className="simple">
                <li>Agenda agreed with your Account Manager before the visit</li>
                <li>Role-based sessions for front desk, billing, and clinical staff</li>
                <li>Follow-up items recorded in your onboarding tracker</li>
              </ul>
            </article>
            <article className="fee-card" aria-labelledby="fee-extended">
              <div className="fee-label">Extended visit</div>
              <h3 className="fee-name" id="fee-extended">Each additional consecutive day</h3>
              <div className="fee-amount">+{formatUsd(ADDITIONAL_DAY_FEE)}<span>per trainer</span></div>
              <p>Further seven-hour days immediately following the first day, for more practice time or more staff.</p>
              <ul className="simple">
                <li>Two days: <b>{formatUsd(ashaFee(2))}</b> per trainer</li>
                <li>Three days: <b>{formatUsd(ashaFee(3))}</b> per trainer</li>
                <li>Time to cover more roles, shifts, or workflows</li>
              </ul>
            </article>
          </div>

          <div className="estimator">
            <fieldset>
              <legend>Visit length (consecutive days)</legend>
              <div className="segmented">
                {DAY_OPTIONS.map((option) => (
                  <label key={option}>
                    <input
                      type="radio"
                      name="onsite-days"
                      value={option}
                      checked={days === option}
                      onChange={() => setDays(option)}
                    />
                    <span>{option} {option === 1 ? "day" : "days"}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div className="estimate-grid" aria-live="polite">
              <div className="estimate-total">
                {formatUsd(ashaFee(days))}
                <small>Trainer fee for {days} consecutive {days === 1 ? "day" : "days"}, per trainer. Travel not included.</small>
              </div>
              <dl className="estimate-breakdown">
                <div><dt>First day</dt><dd>{formatUsd(FIRST_DAY_FEE)}</dd></div>
                <div><dt>Additional days × {additionalDays}</dt><dd>{formatUsd(ADDITIONAL_DAY_FEE * additionalDays)}</dd></div>
                <div><dt>Trainer fee, per trainer</dt><dd>{formatUsd(ashaFee(days))}</dd></div>
              </dl>
            </div>
          </div>
          <p>If a visit needs more than one trainer, the fee applies to each trainer. Longer or non-consecutive visits can be discussed when requesting a quote.</p>
        </Section>

        <Section index={2}>
          <p className="s-sub">A typical outline for a seven-hour day. The final agenda is shaped by your plan and the staff attending.</p>
          <h3 className="sub">First day</h3>
          <ol className="agenda">
            <li><b>Opening</b><span>Review goals with your office lead, confirm who attends each session, and look at open items on the onboarding tracker.</span></li>
            <li><b>Front office</b><span>Practice scheduling, calls, check-in, forms, and payments in the Asha tools your office uses.</span></li>
            <li><b>Billing and insurance</b><span>Work through insurance, claims, and posting tasks with the staff who handle them.</span></li>
            <li><b>Clinical tools</b><span>Cover charting and other clinical features included in your plan.</span></li>
            <li><b>Wrap-up</b><span>Recap what was covered and add follow-up tasks to the tracker for your remote meetings.</span></li>
          </ol>
          <h3 className="sub">Additional days</h3>
          <p>Extra days allow more time to practice with your office’s own examples, train staff on other shifts, revisit harder workflows, and review AI setup and scheduling rules together.</p>
          <h3 className="sub">What the trainer fee covers</h3>
          <ul className="simple">
            <li>One Asha trainer at your office for up to seven hours per day.</li>
            <li>Planning the agenda with your Account Manager before the visit.</li>
            <li>A summary of follow-up items added to your onboarding tracker.</li>
          </ul>
          <p>The fee does not include travel expenses, and it does not turn onboarding into a done-for-you service.</p>
        </Section>

        <Section index={3}>
          <p className="s-sub">Travel is kept separate from the trainer fee so the fee reflects training time only.</p>
          <p>Reasonable airfare, hotel, and ground transportation for each trainer are billed in addition to the trainer fee. Travel is estimated and approved by your practice before anything is booked.</p>
          <p>Travel costs depend on your location, the dates, and the length of the visit, so they are not included in the prices on this page. The exact travel terms are set out in your written quote.</p>
        </Section>

        <Section index={4}>
          <p className="s-sub">On-site visits are arranged through your Account Manager.</p>
          <div className="quote-panel">
            <ol>
              <li>Tell your Account Manager you are interested in an on-site visit and roughly when.</li>
              <li>Agree the number of days, trainers, and staff to be trained.</li>
              <li>Receive a written quote with the trainer fee and estimated travel.</li>
              <li>Approve the quote before dates and travel are booked.</li>
            </ol>
          </div>
          <p>Final terms for each visit, including dates, trainers, and travel, are confirmed in your written quote.</p>
        </Section>

        <footer className="tail"><span>Asha Health, Inc.</span><span>On-site training pricing</span><span>asha.dental</span></footer>
      </div>
    </main>
  );
}
